/**
 * Brand logo components — LearnAI visual identity.
 *
 * Exports a pre-built {@link LogoSet} containing the icon variant
 * ready to pass directly to `<LogoProvider>`.
 *
 * The **icon** variant uses a single transparent PNG placed inside a
 * `bg-primary` container.
 *
 * To update the brand artwork, replace the placeholder PNG in
 * `src/assets/` with the final file — the filename must stay the same:
 *
 * - `logo-icon.png` — compact monogram (transparent bg)
 *
 * @module components/brand/logo
 */

import { cn } from '@ui/lib/utils';
import type { LogoSet } from '@ui/providers/logo';

// ---------------------------------------------------------------------------
// PNG asset import (Vite resolves this to a hashed URL string)
// ---------------------------------------------------------------------------

import logoIcon from '@ui/assets/logo-icon.png';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LogoProps {
	/** Merged onto the outermost element for external sizing. */
	className?: string;
}

// ---------------------------------------------------------------------------
// Icon — compact monogram (transparent PNG on bg-primary)
// ---------------------------------------------------------------------------

/**
 * Theme-agnostic icon — a transparent PNG monogram placed on a
 * `bg-primary` container.
 */
function Icon({ className }: LogoProps) {
	return (
		<div className={cn('flex items-center justify-center rounded-lg bg-primary p-1.5', className)}>
			<img src={logoIcon} alt="LearnAI" draggable={false} className="size-full object-contain" />
		</div>
	);
}

// ---------------------------------------------------------------------------
// Pre-built LogoSet
// ---------------------------------------------------------------------------

/**
 * The complete logo set for the LearnAI brand.
 *
 * Pass this directly to `<LogoProvider logos={logos}>` in each zone's
 * `main.tsx` entry point. The provider + `useLogo()` hook will resolve
 * the correct light / dark rendition automatically.
 *
 * The icon uses the same component for both modes — `bg-primary`
 * handles the colour via CSS custom properties.
 */
const logos: LogoSet = {
	icon: <Icon className="size-full" />,
};

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { Icon, logos };
export type { LogoProps };
